import { resolveType } from '../../../graphql/resolvers/typeResolvers/typeResolvers'

import type { NavigationItem } from '../types'

/**
 * Type guard to check if an item is a NavigationItem using the resolveType logic.
 */
const isNavigationItem = (item: unknown): item is NavigationItem => {
  return resolveType(item as NavigationItem) === 'NavigationItem'
}

/**
 * Utility function to determine active state for navigation items with children.
 * Returns whether the parent should be active and whether any child is active.
 * All Possible States and Their Return Values:
 * 1. Simple Item (No Children)
 * - Active: item.url matches pathname → (parentActive: true, parentHasActiveChild: false).
 * - Inactive: item.url doesn't match → (parentActive: false, parentHasActiveChild: false).
 * 2. Parent Item With Children
 * A. Parent URL matches pathname, no child matches:
 * Result: (parentActive: true, parentHasActiveChild: false).
 * B. Parent URL matches pathname, child also matches:
 * Result: (parentActive: false, parentHasActiveChild: true).
 * C. Parent URL doesn't match, child matches:
 * Result: (parentActive: false, parentHasActiveChild: true).
 * D. Neither parent nor child matches:
 * Result: (parentActive: false, parentHasActiveChild: false).
 */
export const getNavigationItemActive = (
  item: NavigationItem,
  pathname: string,
) => {
  // Check if parent URL directly matches the current pathname
  const parentDirectlyActive =
    item.url === pathname || item.fallbackUrl === pathname

  // Find the active child (if any)
  const activeChild = item.children?.find((child) => {
    if (isNavigationItem(child)) {
      return child.url === pathname
    }
    return false
  })

  const hasActiveChild = Boolean(activeChild)

  // Check if the active child has the same URL as the parent
  const activeChildHasSameUrlAsParent =
    activeChild && isNavigationItem(activeChild)
      ? activeChild.url === item.url || activeChild.url === item.fallbackUrl
      : false

  const parentActive = parentDirectlyActive && !hasActiveChild
  const parentHasActiveChild =
    Boolean(hasActiveChild) &&
    (!parentDirectlyActive || activeChildHasSameUrlAsParent)

  return {
    parentActive,
    parentHasActiveChild,
  }
}

/**
 * Finds the navigation link matching the current URL path and scrolls it into the center of the view.
 * This function should only be called on the client-side as it interacts with the DOM.
 */
export const scrollActiveNavLinkIntoView = (isMobile = false) => {
  // Check if document is defined (client-side only)
  if (typeof document !== 'undefined') {
    // Get the current pathname from the window location
    const currentPath = window.location.pathname + window.location.hash

    const navigationSelector = isMobile
      ? '[aria-label="mobile navigation"]'
      : '[aria-label="desktop navigation"]'

    // Find all anchor elements within the sidenav
    const navLinksNodeList = document.querySelectorAll(
      `${navigationSelector} a[href]`,
    )
    const navLinks = Array.from(navLinksNodeList)

    // Also find CollapsibleGroup buttons that have navigation capability
    // These are buttons with the 'item-link' class that correspond to navigable groups
    const navButtonsNodeList = document.querySelectorAll(
      `${navigationSelector} button.item-link`,
    )
    const navButtons = Array.from(navButtonsNodeList)

    // Check anchor elements first
    for (const link of navLinks) {
      const href = link.getAttribute('href')
      if (href === currentPath) {
        link.scrollIntoView({ behavior: 'auto', block: 'center' })
        return // Stop after finding the first matching link
      }
    }

    // If no anchor match found, check CollapsibleGroup buttons and scroll to the
    // first one that is active
    for (const button of navButtons) {
      if (button.classList.contains('active')) {
        button.scrollIntoView({ behavior: 'auto', block: 'center' })
        return // Stop after finding the first active button
      }
    }
  }
}
